import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Popover,
  Select,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  AirlineSeatReclineNormal,
  CalendarMonth,
  CalendarToday,
  ChevronLeft,
  ChevronRight,
  Close,
  LocalGasStation,
  CloudUpload,
  DirectionsCar,
  EventAvailable,
  LocationOn,
  PhotoCamera,
  PhotoLibrary,
  Settings,
  Build,
} from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useMutation, useQuery } from 'react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { APP_ROUTES } from '../routing/appRoutes';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { ClassicTemplate } from './publicTemplates/ClassicTemplate';
import { MinimalTemplate } from './publicTemplates/MinimalTemplate';
import dayjs, { Dayjs } from 'dayjs';
import { DateInputField } from '../components/DateInputField';
import { normalizeLanguage } from '../i18n/languages';
import { companyApi, companyLocationApi, documentApi, reservationApi, vehicleApi } from '../services/api';
import { DocumentExtractionResult } from '../types/document';
import { CompanyLocation, formatCompanyLocationLabel } from '../types/companyLocation';
import { Vehicle, VehicleSearchParams, VehicleType } from '../types/vehicle';
import { Reservation } from '../types/reservation';
import { CURRENCY_SYMBOL, formatCurrency, formatDate } from '../utils/formatters';
import { compressImageForUpload } from '../utils/imageCompression';
import { STATIC_BASE_URL } from '../utils/runtimeUrls';
import { useTranslation } from 'react-i18next';
import { fuelTypeLabel, vehicleClassLabel, vehicleTypeLabel } from '../i18n/translate';

type BookingFormData = {
  firstName: string;
  lastName: string;
  personalIdNumber: string;
  dateOfBirth: string;
  phoneNumber: string;
  email: string;
  driverLicenseNumber: string;
  licenseIssueDate: string;
  licenseExpiryDate: string;
  licenseCategories: string;
  specialRequests: string;
};

type UploadSide = 'front' | 'back';
type LicenseProcessingState = 'idle' | 'uploading' | 'extracting' | 'completed' | 'failed' | 'skipped';

const emptyBookingForm: BookingFormData = {
  firstName: '',
  lastName: '',
  personalIdNumber: '',
  dateOfBirth: '',
  phoneNumber: '',
  email: '',
  driverLicenseNumber: '',
  licenseIssueDate: '',
  licenseExpiryDate: '',
  licenseCategories: '',
  specialRequests: '',
};

// Sentinel select value for "pick a location from the list" vs. "let the guest type their own".
const OTHER_LOCATION_VALUE = '__OTHER__';
const MONTH_SWIPE_THRESHOLD_PX = 48;
const DEFAULT_PICKUP_TIME = '09:00';
const DEFAULT_RETURN_TIME = '17:00';
const BOOKING_TIME_FIELD_SX = {
  '& .MuiInputBase-input': {
    paddingRight: '4px',
    fontSize: '0.95rem',
  },
  '& input[type="time"]::-webkit-calendar-picker-indicator': {
    width: '14px',
    height: '14px',
    margin: 0,
    position: 'relative',
    right: '-2px',
  },
};

function getBookingPickerMonthDays(month: Dayjs): Array<Dayjs | null> {
  const firstDay = month.startOf('month');
  const leadingEmptyDays = (firstDay.day() + 6) % 7;
  const calendarDays: Array<Dayjs | null> = Array.from({ length: leadingEmptyDays }, () => null);

  for (let day = 1; day <= month.daysInMonth(); day += 1) {
    calendarDays.push(month.date(day));
  }

  while (calendarDays.length % 7 !== 0) {
    calendarDays.push(null);
  }

  return calendarDays;
}

function toPositiveNumber(value: string): number | undefined {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
}

function getVehicleRate(vehicle: Vehicle): number {
  return typeof vehicle.dailyRate === 'string'
    ? parseFloat(vehicle.dailyRate) || 0
    : vehicle.dailyRate || 0;
}

function getDefaultRentalLocation(vehicle: Vehicle, t: (key: string, defaultValue?: string) => string): string {
  if (vehicle.pickupLocation) return vehicle.pickupLocation;
  if (vehicle.currentLocation) return vehicle.currentLocation;

  const storedPickupAddress = [
    vehicle.company?.streetAddress,
    vehicle.company?.city,
  ].filter(Boolean).join(', ');

  return storedPickupAddress || t('fields.pickupLocation', 'Pickup Location');
}

function buildBookingDateTime(date: Dayjs, timeValue: string): string {
  const [hoursText = '0', minutesText = '0'] = timeValue.split(':');
  const hours = Number.parseInt(hoursText, 10);
  const minutes = Number.parseInt(minutesText, 10);

  return date
    .hour(Number.isFinite(hours) ? hours : 0)
    .minute(Number.isFinite(minutes) ? minutes : 0)
    .second(0)
    .format('YYYY-MM-DDTHH:mm:ss');
}

export const SearchPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isMobileView = useMediaQuery(theme.breakpoints.down('sm'));
  const BOOKING_PICKER_WEEKDAYS = [
    t('common.weekdaysShort.mon', 'Mo'),
    t('common.weekdaysShort.tue', 'Tu'),
    t('common.weekdaysShort.wed', 'We'),
    t('common.weekdaysShort.thu', 'Th'),
    t('common.weekdaysShort.fri', 'Fr'),
    t('common.weekdaysShort.sat', 'Sa'),
    t('common.weekdaysShort.sun', 'Su'),
  ];
  const [urlParams] = useSearchParams();
  const navigate = useNavigate();

  const requestedVehicleId = urlParams.get('vehicle') || urlParams.get('vehicleId') || '';
  const requestedPickupLocation = urlParams.get('pickupLocation') || '';
  const requestedReturnLocation = urlParams.get('returnLocation') || '';
  const requestedSpecialRequests = urlParams.get('specialRequests') || '';
  const initialStartDate = urlParams.get('startDate');
  const initialEndDate = urlParams.get('endDate');

  const [vehicleType, setVehicleType] = useState<VehicleType | ''>('');
  const [maxRate, setMaxRate] = useState(urlParams.get('maxRate') || '');
  const [startDate, setStartDate] = useState<Dayjs | null>(
    initialStartDate ? dayjs(initialStartDate) : null
  );
  const [endDate, setEndDate] = useState<Dayjs | null>(
    initialEndDate ? dayjs(initialEndDate) : null
  );
  const [bookingPickerAnchorEl, setBookingPickerAnchorEl] = useState<HTMLElement | null>(null);
  const [bookingPickerOpen, setBookingPickerOpen] = useState(false);
  const [bookingPickerMonth, setBookingPickerMonth] = useState<Dayjs>(
    (initialStartDate ? dayjs(initialStartDate) : dayjs()).startOf('month')
  );
  const [bookingPickerDraftStart, setBookingPickerDraftStart] = useState<Dayjs | null>(
    initialStartDate ? dayjs(initialStartDate) : null
  );
  const [bookingPickerDraftEnd, setBookingPickerDraftEnd] = useState<Dayjs | null>(
    initialEndDate ? dayjs(initialEndDate) : null
  );
  const [detailsVehicle, setDetailsVehicle] = useState<Vehicle | null>(null);
  const [selectedVehicle, setSelectedVehicle] = useState<Vehicle | null>(null);
  const [bookingOpen, setBookingOpen] = useState(false);
  const firstNameFieldRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
  const lastNameFieldRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
  const personalIdFieldRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
  const dateOfBirthFieldRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
  const phoneFieldRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
  const emailFieldRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
  const licenseNumberFieldRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
  const licenseIssueDateFieldRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
  const licenseExpiryDateFieldRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
  const licenseCategoriesFieldRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
  const specialRequestsFieldRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
  const pickupTimeFieldRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
  const returnTimeFieldRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
  const frontLicenseInputRef = useRef<HTMLInputElement | null>(null);
  const frontLicenseCameraInputRef = useRef<HTMLInputElement | null>(null);
  const backLicenseInputRef = useRef<HTMLInputElement | null>(null);
  const bookingPickerTouchStartRef = useRef<{ x: number; y: number } | null>(null);
  const bookingPickerTouchCurrentRef = useRef<{ x: number; y: number } | null>(null);
  const [bookingForm, setBookingForm] = useState<BookingFormData>({
    ...emptyBookingForm,
    specialRequests: requestedSpecialRequests,
  });
  const [driverValidationAttempted, setDriverValidationAttempted] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [successReservation, setSuccessReservation] = useState<Reservation | null>(null);
  const [autoOpenedVehicleId, setAutoOpenedVehicleId] = useState<string | null>(null);
  const [frontLicenseFile, setFrontLicenseFile] = useState<File | null>(null);
  const [backLicenseFile, setBackLicenseFile] = useState<File | null>(null);
  const [frontLicensePreviewUrl, setFrontLicensePreviewUrl] = useState('');
  const [draggingSide, setDraggingSide] = useState<UploadSide | null>(null);
  const [licenseUploadProgress, setLicenseUploadProgress] = useState(0);
  const [licenseProcessingState, setLicenseProcessingState] = useState<LicenseProcessingState>('idle');
  const [licenseStatusMessage, setLicenseStatusMessage] = useState<string | null>(null);
  const [licenseDocumentToken, setLicenseDocumentToken] = useState<string | null>(null);
  const [licenseExtraction, setLicenseExtraction] = useState<DocumentExtractionResult | null>(null);

  // 3-step booking wizard: 0 = driver details, 1 = location & time, 2 = review & submit.
  const [bookingStep, setBookingStep] = useState(0);
  // Step 2: pickup/return are picked from the company's configured locations, or
  // OTHER_LOCATION_VALUE to reveal a free-text field for an address the company doesn't have
  // a branch at.
  const [pickupTime, setPickupTime] = useState(DEFAULT_PICKUP_TIME);
  const [returnTime, setReturnTime] = useState(DEFAULT_RETURN_TIME);
  const [pickupLocationId, setPickupLocationId] = useState('');
  const [pickupLocationOther, setPickupLocationOther] = useState('');
  const [returnSameAsPickup, setReturnSameAsPickup] = useState(true);
  const [returnLocationId, setReturnLocationId] = useState('');
  const [returnLocationOther, setReturnLocationOther] = useState('');
  const [locationValidationAttempted, setLocationValidationAttempted] = useState(false);
  const bookingStepLabels = [
    t('booking.steps.driver', 'Driver details'),
    t('booking.steps.location', 'Location & time'),
    t('booking.steps.review', 'Review & submit'),
  ];
  const activeBookingStepLabel = bookingStepLabels[bookingStep];

  const datesReady = Boolean(startDate && endDate && endDate.isAfter(startDate, 'day'));
  const rentalDays = useMemo(() => {
    if (!startDate || !endDate) return 0;
    return Math.max(endDate.diff(startDate, 'day'), 1);
  }, [endDate, startDate]);
  const dateRangeText = startDate
    ? `${formatDate(startDate)} - ${endDate ? formatDate(endDate) : t('fields.endDate')}`
    : '';
  const isDocumentBusy = licenseProcessingState === 'uploading' || licenseProcessingState === 'extracting';
  const detailsVehicleRate = detailsVehicle ? getVehicleRate(detailsVehicle) : 0;
  const detailsVehicleEstimatedTotal = detailsVehicle && datesReady ? detailsVehicleRate * rentalDays : null;
  const detailsVehicleSpecs = detailsVehicle ? [
    {
      icon: <CalendarToday fontSize="small" />,
      label: t('fields.year', 'Year'),
      value: detailsVehicle.year?.toString() || '-',
    },
    {
      icon: <AirlineSeatReclineNormal fontSize="small" />,
      label: t('fields.seats'),
      value: detailsVehicle.seatingCapacity?.toString() || '-',
    },
    {
      icon: <LocalGasStation fontSize="small" />,
      label: t('fields.fuel'),
      value: detailsVehicle.fuelType ? fuelTypeLabel(t, detailsVehicle.fuelType) : '-',
    },
    {
      icon: <Settings fontSize="small" />,
      label: t('fields.transmission'),
      value: detailsVehicle.transmission || '-',
    },
    {
      icon: <Build fontSize="small" />,
      label: t('fields.engineSize'),
      value: detailsVehicle.engineSize || '-',
    },
  ] : [];
  const formatBookingMonthLabel = (month: Dayjs) => {
    const monthNamesByLanguage = {
      al: ['Janar', 'Shkurt', 'Mars', 'Prill', 'Maj', 'Qershor', 'Korrik', 'Gusht', 'Shtator', 'Tetor', 'Nentor', 'Dhjetor'],
      de: ['Januar', 'Februar', 'Marz', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'],
      en: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
      it: ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'],
    } as const;
    const normalizedLanguage = normalizeLanguage(i18n.resolvedLanguage || i18n.language);
    const monthNames = monthNamesByLanguage[normalizedLanguage] || monthNamesByLanguage.en;
    return `${monthNames[month.month()]} ${month.year()}`;
  };

  const resetDriverLicenseUpload = () => {
    setFrontLicenseFile(null);
    setBackLicenseFile(null);
    setDraggingSide(null);
    setLicenseUploadProgress(0);
    setLicenseProcessingState('idle');
    setLicenseStatusMessage(null);
    setLicenseDocumentToken(null);
    setLicenseExtraction(null);
  };

  const resetBookingFormState = () => {
    setBookingForm({
      ...emptyBookingForm,
      specialRequests: requestedSpecialRequests,
    });
    setDriverValidationAttempted(false);
  };

  const searchParams = useMemo<VehicleSearchParams & { size: number; sortBy: string; sortDir: string }>(() => {
    const params: VehicleSearchParams & { size: number; sortBy: string; sortDir: string } = {
      size: 100,
      sortBy: 'dailyRate',
      sortDir: 'asc',
    };

    if (vehicleType) params.type = vehicleType;
    const maxRateValue = toPositiveNumber(maxRate);
    if (maxRateValue) params.maxRate = maxRateValue;
    if (startDate) params.startDate = startDate.format('YYYY-MM-DD');
    if (endDate) params.endDate = endDate.format('YYYY-MM-DD');

    return params;
  }, [endDate, maxRate, startDate, vehicleType]);

  const {
    data: vehicles = [],
    isLoading,
    error,
  } = useQuery(
    ['bookingVehicles', searchParams],
    () => vehicleApi.search(searchParams),
    {
      enabled: datesReady,
      keepPreviousData: true,
    }
  );

  const { data: tenantCompany, isLoading: isTenantCompanyLoading } = useQuery(
    'publicTenantCompany',
    () => companyApi.getTenant(),
    {
      staleTime: 5 * 60 * 1000,
      retry: false,
    }
  );

  const [logoLoadError, setLogoLoadError] = useState(false);

  const tenantLogoUrl = tenantCompany?.logoUrl
    ? tenantCompany.logoUrl.startsWith('http')
      ? tenantCompany.logoUrl
      : `${STATIC_BASE_URL}${tenantCompany.logoUrl}`
    : null;

  const tenantLocationSummary = [tenantCompany?.city, tenantCompany?.country]
    .filter(Boolean)
    .join(', ');

  const tenantWhatsappHref = tenantCompany?.whatsappNumber
    ? `https://wa.me/${tenantCompany.whatsappNumber.replace(/[^\d]/g, '')}`
    : null;

  // Date-independent fleet browse, shown before rental dates are chosen so the public
  // page never has an empty fleet section.
  const { data: quickFleet = [] } = useQuery(
    'quickFleetBrowse',
    () => vehicleApi.quickSearch({ limit: 48 }),
    { staleTime: 5 * 60 * 1000 }
  );

  // Company's configured pickup/return points, for the booking dialog's location dropdowns.
  const { data: companyLocations = [] } = useQuery(
    'publicCompanyLocations',
    () => companyLocationApi.getPublic(),
    { staleTime: 5 * 60 * 1000 }
  );

  const resolvePickupLocationText = (locationId: string, otherText: string) => {
    if (locationId === OTHER_LOCATION_VALUE) return otherText.trim();
    const location = companyLocations.find((candidate) => candidate.id.toString() === locationId);
    return location ? formatCompanyLocationLabel(location) : '';
  };

  const pickupLocationText = resolvePickupLocationText(pickupLocationId, pickupLocationOther);
  const returnLocationText = returnSameAsPickup
    ? pickupLocationText
    : resolvePickupLocationText(returnLocationId, returnLocationOther);

  const createBookingMutation = useMutation(reservationApi.createGuestReservation, {
    onSuccess: (reservation: Reservation) => {
      setSuccessReservation(reservation);
      setBookingOpen(false);
      setSelectedVehicle(null);
      resetBookingFormState();
      resetDriverLicenseUpload();
      resetBookingWizardState();
      setFormError(null);
    },
    onError: (err: any) => {
      setFormError(
        err.response?.data?.error ||
        err.response?.data?.message ||
        t('booking.messages.createFailed', 'Could not create the booking request.')
      );
    },
  });

  const uploadDriverLicenseMutation = useMutation(documentApi.uploadPublicAndExtract, {
    onSuccess: (response) => {
      setLicenseUploadProgress(100);
      setLicenseDocumentToken(response.temporaryDocumentToken || null);
      setLicenseExtraction(response.extraction || null);
      setLicenseStatusMessage(response.message || null);

      const nextState: LicenseProcessingState =
        response.processingStatus === 'COMPLETED'
          ? 'completed'
          : response.processingStatus === 'SKIPPED'
            ? 'skipped'
            : 'failed';
      setLicenseProcessingState(nextState);

      const extractedLicense = response.extraction?.driverLicense;
      if (extractedLicense) {
        setBookingForm((current) => ({
          ...current,
          firstName: extractedLicense.firstName || current.firstName,
          lastName: extractedLicense.lastName || current.lastName,
          personalIdNumber: extractedLicense.personalIdNumber || current.personalIdNumber,
          dateOfBirth: extractedLicense.dateOfBirth || current.dateOfBirth,
          driverLicenseNumber: extractedLicense.licenseNumber || current.driverLicenseNumber,
          licenseIssueDate: extractedLicense.dateOfIssue || current.licenseIssueDate,
          licenseExpiryDate: extractedLicense.expiryDate || current.licenseExpiryDate,
          licenseCategories: extractedLicense.licenseCategories || current.licenseCategories,
        }));
      }
    },
    onError: (err: any) => {
      setLicenseDocumentToken(null);
      setLicenseExtraction(null);
      setLicenseProcessingState('failed');
      setLicenseStatusMessage(
        err.response?.data?.error ||
        err.response?.data?.message ||
        t('booking.document.extractionFailed', 'We could not process the uploaded document. You can still complete the form manually.')
      );
    },
  });

  useEffect(() => {
    if (!frontLicenseFile) {
      setFrontLicensePreviewUrl('');
      return undefined;
    }

    const nextUrl = URL.createObjectURL(frontLicenseFile);
    setFrontLicensePreviewUrl(nextUrl);
    return () => URL.revokeObjectURL(nextUrl);
  }, [frontLicenseFile]);

  useEffect(() => {
    if (!requestedVehicleId || !datesReady || autoOpenedVehicleId === requestedVehicleId) {
      return;
    }

    const vehicle = vehicles.find((candidate) => candidate.id.toString() === requestedVehicleId);
    if (vehicle) {
      openBookingDialog(vehicle);
      setAutoOpenedVehicleId(requestedVehicleId);
    }
  }, [autoOpenedVehicleId, datesReady, requestedVehicleId, vehicles]);

  const handleOpenBookingDatePicker = (event?: React.MouseEvent<HTMLElement>) => {
    const visibleMonth = (startDate || dayjs()).startOf('month');
    setBookingPickerDraftStart(startDate);
    setBookingPickerDraftEnd(endDate);
    setBookingPickerMonth(visibleMonth);
    setBookingPickerAnchorEl(isMobileView ? null : (event?.currentTarget ?? null));
    setBookingPickerOpen(true);
  };

  const handleCloseBookingDatePicker = () => {
    setBookingPickerAnchorEl(null);
    setBookingPickerOpen(false);
  };

  const applyBookingPickerRange = (nextStartDate: Dayjs, nextEndDate: Dayjs | null) => {
    setStartDate(nextStartDate.startOf('day'));
    setEndDate(nextEndDate ? nextEndDate.startOf('day') : null);
    setSuccessReservation(null);
  };

  const handleBookingDateSelect = (day: Dayjs) => {
    const selectedDay = day.startOf('day');
    if (selectedDay.isBefore(dayjs().startOf('day'), 'day')) {
      return;
    }

    if (!bookingPickerDraftStart || bookingPickerDraftEnd) {
      setBookingPickerDraftStart(selectedDay);
      setBookingPickerDraftEnd(null);
      applyBookingPickerRange(selectedDay, null);
      return;
    }

    if (selectedDay.isSame(bookingPickerDraftStart, 'day') || selectedDay.isBefore(bookingPickerDraftStart, 'day')) {
      setBookingPickerDraftStart(selectedDay);
      setBookingPickerDraftEnd(null);
      applyBookingPickerRange(selectedDay, null);
      return;
    }

    setBookingPickerDraftEnd(selectedDay);
    applyBookingPickerRange(bookingPickerDraftStart, selectedDay);
    handleCloseBookingDatePicker();
  };

  const shiftBookingPickerMonth = (direction: 'previous' | 'next') => {
    setBookingPickerMonth((currentMonth) =>
      currentMonth.add(direction === 'next' ? 1 : -1, 'month').startOf('month')
    );
  };

  const handleBookingPickerTouchStart = (event: React.TouchEvent<HTMLElement>) => {
    if (!isMobileView) {
      return;
    }
    const touch = event.touches[0];
    bookingPickerTouchStartRef.current = { x: touch.clientX, y: touch.clientY };
    bookingPickerTouchCurrentRef.current = { x: touch.clientX, y: touch.clientY };
  };

  const handleBookingPickerTouchMove = (event: React.TouchEvent<HTMLElement>) => {
    if (!bookingPickerTouchStartRef.current) {
      return;
    }
    const touch = event.touches[0];
    bookingPickerTouchCurrentRef.current = { x: touch.clientX, y: touch.clientY };
  };

  const handleBookingPickerTouchEnd = () => {
    const start = bookingPickerTouchStartRef.current;
    const current = bookingPickerTouchCurrentRef.current;
    bookingPickerTouchStartRef.current = null;
    bookingPickerTouchCurrentRef.current = null;

    if (!isMobileView || !start || !current) {
      return;
    }

    const deltaX = current.x - start.x;
    const deltaY = current.y - start.y;

    if (Math.abs(deltaX) < MONTH_SWIPE_THRESHOLD_PX || Math.abs(deltaX) <= Math.abs(deltaY)) {
      return;
    }

    shiftBookingPickerMonth(deltaX < 0 ? 'next' : 'previous');
  };

  const renderBookingPickerMonth = (month: Dayjs, showPreviousButton: boolean, showNextButton: boolean) => {
    const today = dayjs().startOf('day');
    const days = getBookingPickerMonthDays(month);
    const bookingDaySize = isMobileView ? 42 : 36;

    return (
      <Box sx={{ minWidth: 0 }}>
        <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
          {showPreviousButton && (
            <IconButton
              size="small"
              aria-label={t('search.bookingPreviousMonths')}
              onClick={() => shiftBookingPickerMonth('previous')}
              sx={{ position: 'absolute', left: 0 }}
            >
              <ChevronLeft fontSize="small" />
            </IconButton>
          )}
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            {formatBookingMonthLabel(month)}
          </Typography>
          {showNextButton && (
            <IconButton
              size="small"
              aria-label={t('search.bookingNextMonths')}
              onClick={() => shiftBookingPickerMonth('next')}
              sx={{ position: 'absolute', right: 0 }}
            >
              <ChevronRight fontSize="small" />
            </IconButton>
          )}
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: `repeat(7, ${bookingDaySize}px)`,
            justifyContent: 'center',
            rowGap: isMobileView ? 1 : 0.75,
            columnGap: isMobileView ? 0.75 : 0.5,
          }}
        >
          {BOOKING_PICKER_WEEKDAYS.map((weekday) => (
            <Typography
              key={`${month.format('YYYY-MM')}-${weekday}`}
              variant="caption"
              align="center"
              sx={{
                color: 'text.secondary',
                fontWeight: 600,
                lineHeight: `${Math.max(bookingDaySize - 8, 28)}px`,
              }}
            >
              {weekday}
            </Typography>
          ))}

          {days.map((day, index) => {
            if (!day) {
              return (
                <Box
                  key={`${month.format('YYYY-MM')}-empty-${index}`}
                  sx={{ width: bookingDaySize, height: bookingDaySize }}
                />
              );
            }

            const isSelectedStart = Boolean(bookingPickerDraftStart?.isSame(day, 'day'));
            const isSelectedEnd = Boolean(bookingPickerDraftEnd?.isSame(day, 'day'));
            const isInRange = Boolean(
              bookingPickerDraftStart &&
                bookingPickerDraftEnd &&
                day.isAfter(bookingPickerDraftStart, 'day') &&
                day.isBefore(bookingPickerDraftEnd, 'day')
            );
            const isDisabled = day.isBefore(today, 'day');

            return (
              <Box
                key={day.format('YYYY-MM-DD')}
                component="button"
                type="button"
                disabled={isDisabled}
                aria-label={day.format('DD MMMM YYYY')}
                onClick={() => handleBookingDateSelect(day)}
                sx={{
                  width: bookingDaySize,
                  height: bookingDaySize,
                  p: 0,
                  border: '1px solid',
                  borderColor: isSelectedStart || isSelectedEnd ? 'primary.main' : 'transparent',
                  borderRadius: isInRange ? 1 : '50%',
                  bgcolor: isSelectedStart || isSelectedEnd ? 'primary.main' : (isInRange ? '#e7f1ff' : 'transparent'),
                  color: isSelectedStart || isSelectedEnd ? 'primary.contrastText' : (isDisabled ? 'text.disabled' : 'text.primary'),
                  cursor: isDisabled ? 'default' : 'pointer',
                  font: 'inherit',
                  fontWeight: isSelectedStart || isSelectedEnd || day.isSame(today, 'day') ? 700 : 400,
                  opacity: isDisabled ? 0.42 : 1,
                  '&:hover': {
                    bgcolor: isDisabled
                      ? 'transparent'
                      : (isSelectedStart || isSelectedEnd ? 'primary.dark' : 'action.hover'),
                  },
                }}
              >
                {day.date()}
              </Box>
            );
          })}
        </Box>
      </Box>
    );
  };

  const handleOpenVehicleDetails = (vehicle: Vehicle) => {
    setDetailsVehicle(vehicle);
  };

  const handleCloseVehicleDetails = () => {
    setDetailsVehicle(null);
  };

  const findCompanyLocationByText = (text: string): CompanyLocation | undefined =>
    companyLocations.find(
      (location) => formatCompanyLocationLabel(location) === text || location.name === text
    );

  const openBookingDialog = (vehicle: Vehicle) => {
    setSelectedVehicle(vehicle);
    setBookingOpen(true);
    setBookingStep(0);
    setFormError(null);
    setDriverValidationAttempted(false);
    setLocationValidationAttempted(false);
    setPickupTime(DEFAULT_PICKUP_TIME);
    setReturnTime(DEFAULT_RETURN_TIME);

    const primaryLocation =
      companyLocations.find((location) => location.primaryCompanyAddress) || companyLocations[0];

    if (requestedPickupLocation) {
      const matched = findCompanyLocationByText(requestedPickupLocation);
      setPickupLocationId(matched ? matched.id.toString() : OTHER_LOCATION_VALUE);
      setPickupLocationOther(matched ? '' : requestedPickupLocation);
    } else if (primaryLocation) {
      setPickupLocationId(primaryLocation.id.toString());
      setPickupLocationOther('');
    } else {
      // No locations configured yet - fall back to a free-text field pre-filled from the vehicle.
      setPickupLocationId(OTHER_LOCATION_VALUE);
      setPickupLocationOther(getDefaultRentalLocation(vehicle, t));
    }

    if (requestedReturnLocation && requestedReturnLocation !== requestedPickupLocation) {
      setReturnSameAsPickup(false);
      const matched = findCompanyLocationByText(requestedReturnLocation);
      setReturnLocationId(matched ? matched.id.toString() : OTHER_LOCATION_VALUE);
      setReturnLocationOther(matched ? '' : requestedReturnLocation);
    } else {
      setReturnSameAsPickup(true);
      setReturnLocationId('');
      setReturnLocationOther('');
    }
  };

  const handleVehicleSelect = (vehicle: Vehicle) => {
    setDetailsVehicle(null);
    openBookingDialog(vehicle);
  };

  const resetBookingWizardState = () => {
    setBookingStep(0);
    setPickupTime(DEFAULT_PICKUP_TIME);
    setReturnTime(DEFAULT_RETURN_TIME);
    setPickupLocationId('');
    setPickupLocationOther('');
    setReturnSameAsPickup(true);
    setReturnLocationId('');
    setReturnLocationOther('');
    setLocationValidationAttempted(false);
  };

  const handleCloseBooking = () => {
    if (createBookingMutation.isLoading || isDocumentBusy) return;
    setBookingOpen(false);
    setSelectedVehicle(null);
    resetBookingFormState();
    resetDriverLicenseUpload();
    resetBookingWizardState();
    setFormError(null);
  };

  const resetExtractionStateForNewSelection = () => {
    setLicenseUploadProgress(0);
    setLicenseProcessingState('idle');
    setLicenseStatusMessage(null);
    setLicenseDocumentToken(null);
    setLicenseExtraction(null);
  };

  const handleDocumentFileSelected = async (side: UploadSide, file: File | null) => {
    const processedFile = file ? await compressImageForUpload(file) : null;
    if (side === 'front') {
      setFrontLicenseFile(processedFile);
    } else {
      setBackLicenseFile(processedFile);
    }
    resetExtractionStateForNewSelection();
  };

  const handleDriverLicenseDrop = (side: UploadSide, event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDraggingSide(null);

    const file = event.dataTransfer.files?.[0];
    if (!file) return;

    handleDocumentFileSelected(side, file);
  };

  const handleRemoveDriverLicenseFile = (side: UploadSide) => {
    if (side === 'front') {
      setFrontLicenseFile(null);
      if (frontLicenseInputRef.current) {
        frontLicenseInputRef.current.value = '';
      }
      if (frontLicenseCameraInputRef.current) {
        frontLicenseCameraInputRef.current.value = '';
      }
    } else {
      setBackLicenseFile(null);
      if (backLicenseInputRef.current) {
        backLicenseInputRef.current.value = '';
      }
    }

    resetExtractionStateForNewSelection();
  };

  const handleUploadDriverLicense = () => {
    if (!selectedVehicle || !frontLicenseFile) {
      setLicenseStatusMessage(
        t('booking.document.frontRequired', 'Please upload the driver license first.')
      );
      setLicenseProcessingState('failed');
      return;
    }

    setLicenseUploadProgress(0);
    setLicenseProcessingState('uploading');
    setLicenseStatusMessage(
      t('booking.document.uploading', 'Uploading the document securely...')
    );

    uploadDriverLicenseMutation.mutate({
      vehicleId: selectedVehicle.id,
      documentType: 'DRIVER_LICENSE',
      frontFile: frontLicenseFile,
      backFile: backLicenseFile,
      onUploadProgress: (progressPercent: number) => {
        setLicenseUploadProgress(progressPercent);
        if (progressPercent >= 100) {
          setLicenseProcessingState('extracting');
          setLicenseStatusMessage(
            t('booking.document.extracting', 'AI is reading the document and extracting the fields...')
          );
        } else {
          setLicenseProcessingState('uploading');
        }
      },
    });
  };

  const triggerFrontLicensePicker = (source: 'gallery' | 'camera') => {
    const inputRef = source === 'camera' ? frontLicenseCameraInputRef : frontLicenseInputRef;
    if (inputRef.current) {
      inputRef.current.value = '';
      inputRef.current.click();
    }
  };

  const handleBookingFormChange = (field: keyof BookingFormData, value: string) => {
    setBookingForm((current) => ({ ...current, [field]: value }));
  };

  const focusBookingField = (
    fieldRef: React.MutableRefObject<HTMLInputElement | HTMLTextAreaElement | null>
  ) => {
    fieldRef.current?.focus();
  };

  const handleAdvanceBookingField = (
    event: React.KeyboardEvent<HTMLElement>,
    nextFieldRef: React.MutableRefObject<HTMLInputElement | HTMLTextAreaElement | null>
  ) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      focusBookingField(nextFieldRef);
    }
  };

  const driverRequiredFields: Array<keyof BookingFormData> = [
    'firstName',
    'lastName',
    'personalIdNumber',
    'dateOfBirth',
    'phoneNumber',
    'driverLicenseNumber',
    'licenseIssueDate',
    'licenseExpiryDate',
    'licenseCategories',
  ];

  const getMissingDriverFields = (): Array<keyof BookingFormData> =>
    driverRequiredFields.filter((field) => !bookingForm[field].trim());

  const isDriverFieldMissing = (field: keyof BookingFormData) =>
    driverValidationAttempted && !bookingForm[field].trim();

  const hasDriverEmailError =
    Boolean(bookingForm.email.trim()) &&
    !/\S+@\S+\.\S+/.test(bookingForm.email);

  const isDriverEmailInvalid = driverValidationAttempted && hasDriverEmailError;

  const focusDriverField = (field: keyof BookingFormData | 'email') => {
    switch (field) {
      case 'firstName':
        focusBookingField(firstNameFieldRef);
        break;
      case 'lastName':
        focusBookingField(lastNameFieldRef);
        break;
      case 'personalIdNumber':
        focusBookingField(personalIdFieldRef);
        break;
      case 'dateOfBirth':
        focusBookingField(dateOfBirthFieldRef);
        break;
      case 'phoneNumber':
        focusBookingField(phoneFieldRef);
        break;
      case 'email':
        focusBookingField(emailFieldRef);
        break;
      case 'driverLicenseNumber':
        focusBookingField(licenseNumberFieldRef);
        break;
      case 'licenseIssueDate':
        focusBookingField(licenseIssueDateFieldRef);
        break;
      case 'licenseExpiryDate':
        focusBookingField(licenseExpiryDateFieldRef);
        break;
      case 'licenseCategories':
        focusBookingField(licenseCategoriesFieldRef);
        break;
      default:
        break;
    }
  };

  const revealDriverValidationErrors = () => {
    setDriverValidationAttempted(true);

    const missingFields = getMissingDriverFields();
    if (missingFields.length > 0) {
      focusDriverField(missingFields[0]);
      return true;
    }

    if (hasDriverEmailError) {
      focusDriverField('email');
      return true;
    }

    return false;
  };

  const isPickupLocationMissing = locationValidationAttempted && !pickupLocationId;
  const isPickupLocationOtherMissing =
    locationValidationAttempted &&
    pickupLocationId === OTHER_LOCATION_VALUE &&
    !pickupLocationOther.trim();
  const isReturnLocationMissing =
    locationValidationAttempted &&
    !returnSameAsPickup &&
    !returnLocationId;
  const isReturnLocationOtherMissing =
    locationValidationAttempted &&
    !returnSameAsPickup &&
    returnLocationId === OTHER_LOCATION_VALUE &&
    !returnLocationOther.trim();
  const isPickupTimeMissing = locationValidationAttempted && !pickupTime.trim();
  const isReturnTimeMissing = locationValidationAttempted && !returnTime.trim();

  const revealLocationValidationErrors = () => {
    setLocationValidationAttempted(true);

    if (!pickupTime.trim()) {
      focusBookingField(pickupTimeFieldRef);
      return true;
    }

    if (!returnTime.trim()) {
      focusBookingField(returnTimeFieldRef);
      return true;
    }

    return (
      !pickupLocationId ||
      (pickupLocationId === OTHER_LOCATION_VALUE && !pickupLocationOther.trim()) ||
      (!returnSameAsPickup && !returnLocationId) ||
      (!returnSameAsPickup &&
        returnLocationId === OTHER_LOCATION_VALUE &&
        !returnLocationOther.trim())
    );
  };

  // Step 1: driver details (form fields + license upload).
  const validateDriverStep = () => {
    if (isDocumentBusy) {
      return t('booking.document.waitForProcessing', 'Please wait for the document upload and extraction to finish before submitting.');
    }

    if (frontLicenseFile && !licenseDocumentToken) {
      return t(
        'booking.document.finishUpload',
        'Please finish the driver license upload before booking, or remove the selected file and continue manually.'
      );
    }

    return null;
  };

  // Step 2: location & time. Pickup/return are picked from the company's locations, or
  // OTHER_LOCATION_VALUE with a free-text address; dates come from the search filters above.
  const validateLocationStep = () => {
    if (!datesReady || !startDate || !endDate) {
      return t('booking.validation.validDates', 'Please select a valid rental date range.');
    }

    if (!pickupLocationId) {
      return t('booking.validation.pickupRequired', 'Please select a pickup location.');
    }
    if (pickupLocationId === OTHER_LOCATION_VALUE && !pickupLocationOther.trim()) {
      return t('booking.validation.pickupOtherRequired', 'Please enter the pickup address.');
    }
    if (!pickupTime.trim()) {
      return t('booking.validation.pickupTimeRequired', 'Please choose a pickup time.');
    }
    if (!returnTime.trim()) {
      return t('booking.validation.returnTimeRequired', 'Please choose a return time.');
    }

    if (!returnSameAsPickup) {
      if (!returnLocationId) {
        return t('booking.validation.returnRequired', 'Please select a return location.');
      }
      if (returnLocationId === OTHER_LOCATION_VALUE && !returnLocationOther.trim()) {
        return t('booking.validation.returnOtherRequired', 'Please enter the return address.');
      }
    }

    if (!selectedVehicle) {
      return t('booking.validation.vehicleRequired', 'Please select a vehicle.');
    }

    return null;
  };

  const bookingStepValidators = [validateDriverStep, validateLocationStep];

  const handleBookingNext = () => {
    if (bookingStep === 0 && revealDriverValidationErrors()) {
      setFormError(null);
      return;
    }
    if (bookingStep === 1 && revealLocationValidationErrors()) {
      setFormError(null);
      return;
    }

    const validationError = bookingStepValidators[bookingStep]?.();
    if (validationError) {
      setFormError(validationError);
      return;
    }
    setFormError(null);
    if (bookingStep === 0) {
      setDriverValidationAttempted(false);
    }
    if (bookingStep === 1) {
      setLocationValidationAttempted(false);
    }
    setBookingStep((current) => Math.min(current + 1, 2));
  };

  const handleBookingBack = () => {
    setFormError(null);
    setBookingStep((current) => Math.max(current - 1, 0));
  };

  const handleBookingFormSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    if (bookingStep < 2) {
      handleBookingNext();
      return;
    }

    if (revealDriverValidationErrors()) {
      setFormError(null);
      return;
    }
    if (revealLocationValidationErrors()) {
      setFormError(null);
      return;
    }

    const validationError = validateDriverStep() || validateLocationStep();
    if (validationError) {
      setFormError(validationError);
      return;
    }

    if (!selectedVehicle || !startDate || !endDate) return;

    // Only a custom "Other" address needs calling out in the notes - staff already see the
    // reservation's real pickup/return company location elsewhere, so echoing one of our own
    // known locations back into notes would just be noise. A custom address has nowhere else
    // to live, so that's the one case it's appended (never replacing whatever the guest already
    // wrote - see ReservationService.createGuestReservation, which mirrors specialRequests into
    // Notes as-is). When return mirrors pickup, an "Other" pickup address already covers it.
    const pickupIsOther = pickupLocationId === OTHER_LOCATION_VALUE;
    const returnIsOther = !returnSameAsPickup && returnLocationId === OTHER_LOCATION_VALUE;
    const notesLines = [
      bookingForm.specialRequests.trim(),
      pickupIsOther
        ? `${t('booking.steps.pickupLocationNoteLabel', 'Pickup Location')}: ${pickupLocationText}`
        : '',
      returnIsOther
        ? `${t('booking.steps.returnLocationNoteLabel', 'Return Location')}: ${returnLocationText}`
        : '',
    ].filter(Boolean);

    createBookingMutation.mutate({
      driverName: `${bookingForm.firstName.trim()} ${bookingForm.lastName.trim()}`,
      firstName: bookingForm.firstName.trim(),
      lastName: bookingForm.lastName.trim(),
      driverEmail: bookingForm.email.trim() || undefined,
      driverPhone: bookingForm.phoneNumber.trim(),
      personalIdNumber: bookingForm.personalIdNumber.trim(),
      dateOfBirth: bookingForm.dateOfBirth,
      driverLicenseNumber: bookingForm.driverLicenseNumber.trim(),
      licenseIssueDate: bookingForm.licenseIssueDate,
      licenseExpiryDate: bookingForm.licenseExpiryDate,
      licenseCategories: bookingForm.licenseCategories.trim(),
      vehicleId: selectedVehicle.id,
      startDate: startDate.format('YYYY-MM-DD'),
      endDate: endDate.format('YYYY-MM-DD'),
      pickupTime: buildBookingDateTime(startDate, pickupTime),
      returnTime: buildBookingDateTime(endDate, returnTime),
      pickupLocation: pickupLocationText,
      returnLocation: returnLocationText,
      specialRequests: notesLines.join('\n'),
      paymentMethod: 'PAY_AT_PICKUP',
      temporaryDocumentTokens: licenseDocumentToken ? [licenseDocumentToken] : undefined,
    });
  };

  const resetFilters = () => {
    setVehicleType('');
    setMaxRate('');
    setStartDate(null);
    setEndDate(null);
    setSuccessReservation(null);
  };

  // Pickup/return location picker for step 2: a dropdown of the company's configured
  // locations (name + grey address) plus an "Other" option that reveals a free-text field.
  const renderLocationSelect = ({
    label,
    otherLabel,
    idValue,
    onIdChange,
    otherValue,
    onOtherChange,
    hasError,
    hasOtherError,
  }: {
    label: string;
    otherLabel: string;
    idValue: string;
    onIdChange: (value: string) => void;
    otherValue: string;
    onOtherChange: (value: string) => void;
    hasError: boolean;
    hasOtherError: boolean;
  }) => (
    <Stack spacing={2.5}>
      <FormControl fullWidth required size="small" error={hasError}>
        <InputLabel>{label}</InputLabel>
        <Select
          label={label}
          value={idValue}
          onChange={(event) => onIdChange(event.target.value as string)}
          renderValue={(value) => {
            if (value === OTHER_LOCATION_VALUE) {
              return t('booking.steps.otherLocation', 'Other (enter address)');
            }
            const location = companyLocations.find((candidate) => candidate.id.toString() === value);
            return location?.name || '';
          }}
        >
          {companyLocations.map((location) => (
            <MenuItem key={location.id} value={location.id.toString()}>
              <Box>
                <Typography variant="body2">{location.name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {[location.address, location.city].filter(Boolean).join(', ')}
                </Typography>
              </Box>
            </MenuItem>
          ))}
          <MenuItem value={OTHER_LOCATION_VALUE}>
            {t('booking.steps.otherLocation', 'Other (enter address)')}
          </MenuItem>
        </Select>
      </FormControl>

      {idValue === OTHER_LOCATION_VALUE && (
        <TextField
          fullWidth
          required
          size="small"
          label={otherLabel}
          value={otherValue}
          onChange={(event) => onOtherChange(event.target.value)}
          error={hasOtherError}
          InputProps={{ startAdornment: <LocationOn fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} /> }}
        />
      )}
    </Stack>
  );

  const renderVehicleCard = (vehicle: Vehicle, withDates: boolean) => {
    const dailyRate = getVehicleRate(vehicle);
    return (
      <Paper
        key={vehicle.id}
        variant="outlined"
        sx={{
          borderRadius: { xs: 3.5, sm: 3 },
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          transition: 'box-shadow 0.2s, transform 0.2s',
          '&:hover': { boxShadow: '0 12px 28px rgba(15,23,42,0.12)', transform: 'translateY(-2px)' },
        }}
      >
        <Box
          sx={{
            height: { xs: 140, sm: 160 },
            bgcolor: 'grey.100',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
          }}
        >
          {vehicle.imageUrls?.[0] ? (
            <Box
              component="img"
              src={vehicle.imageUrls[0]}
              alt={`${vehicle.make} ${vehicle.model}`}
              sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : (
            <DirectionsCar sx={{ fontSize: 48 }} color="action" />
          )}
        </Box>

        <Stack spacing={1.1} sx={{ p: { xs: 1.5, sm: 2 }, flexGrow: 1 }}>
          <Box>
            <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.2 }}>
              {vehicle.make} {vehicle.model}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {vehicle.year}
              {vehicle.licensePlate ? ` · ${vehicle.licensePlate}` : ''}
            </Typography>
          </Box>

          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
            <Chip size="small" label={`${vehicle.seatingCapacity || '-'} ${t('fields.seats')}`} variant="outlined" />
            <Chip size="small" label={vehicleTypeLabel(t, vehicle.type)} variant="outlined" />
            <Chip size="small" label={vehicleClassLabel(t, vehicle.vehicleClass)} variant="outlined" />
          </Stack>

          <Box sx={{ flexGrow: 1 }} />

          <Box>
            {withDates ? (
              <Typography variant="caption" color="text.secondary">
                {t('fields.estimatedTotal')}
              </Typography>
            ) : null}
            <Typography variant="h6" fontWeight={800} color="primary.main">
              {withDates ? formatCurrency(dailyRate * rentalDays) : formatCurrency(dailyRate)}
              {!withDates && (
                <Typography component="span" variant="body2" color="text.secondary">
                  {' '}
                  / {t('search.perDay', 'day')}
                </Typography>
              )}
            </Typography>
          </Box>

          <Stack direction="row" spacing={1}>
            <Button
              fullWidth
              size={isMobileView ? 'medium' : 'small'}
              variant="outlined"
              onClick={() => handleOpenVehicleDetails(vehicle)}
            >
              {t('search.detailsAction', 'Details')}
            </Button>
            <Button
              fullWidth
              size={isMobileView ? 'medium' : 'small'}
              variant="contained"
              onClick={(event) =>
                withDates ? handleVehicleSelect(vehicle) : handleOpenBookingDatePicker(event)
              }
            >
              {withDates ? t('search.bookAction', 'Book') : t('search.checkAvailability', 'Dates')}
            </Button>
          </Stack>
        </Stack>
      </Paper>
    );
  };

  const vehicleGridSx = {
    display: 'grid',
    gridTemplateColumns: {
      xs: '1fr',
      sm: 'repeat(2, minmax(0, 1fr))',
      md: 'repeat(3, minmax(0, 1fr))',
      lg: 'repeat(4, minmax(0, 1fr))',
    },
    gap: 2.5,
  } as const;

  const renderResultBody = () => {
    if (!datesReady) {
      if (quickFleet.length === 0) {
        return (
          <Box sx={{ py: 5, textAlign: 'center' }}>
            <CalendarToday sx={{ color: 'text.secondary', mb: 1 }} />
            <Typography color="text.secondary">
              {t('booking.selectDatesPrompt', 'Rental dates required')}
            </Typography>
          </Box>
        );
      }

      return <Box sx={vehicleGridSx}>{quickFleet.map((vehicle) => renderVehicleCard(vehicle, false))}</Box>;
    }

    if (isLoading) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 5 }}>
          <CircularProgress size={28} />
        </Box>
      );
    }

    if (error) {
      return (
        <Alert severity="error" sx={{ m: 2 }}>
          {t('search.errorLoading')} {error instanceof Error ? error.message : t('search.unknownError')}
        </Alert>
      );
    }

    if (vehicles.length === 0) {
      return (
        <Box sx={{ py: 5, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            {t('search.empty')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('search.emptyHint')}
          </Typography>
        </Box>
      );
    }

    return <Box sx={vehicleGridSx}>{vehicles.map((vehicle) => renderVehicleCard(vehicle, true))}</Box>;
  };

  const isMinimalTemplate = tenantCompany?.publicPageTemplate === 'MINIMAL';

  const bookingPickerContent = (
    <Stack spacing={2.25}>
      {isMobileView && (
        <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2.5, bgcolor: 'grey.50' }}>
          <Grid container spacing={1.25}>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">
                {t('fields.startDate', 'Start date')}
              </Typography>
              <Typography variant="body2" fontWeight={700}>
                {bookingPickerDraftStart ? formatDate(bookingPickerDraftStart) : '—'}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">
                {t('fields.endDate')}
              </Typography>
              <Typography variant="body2" fontWeight={700}>
                {bookingPickerDraftEnd ? formatDate(bookingPickerDraftEnd) : '—'}
              </Typography>
            </Grid>
          </Grid>
        </Paper>
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: isMobileView ? '1fr' : { xs: '1fr', md: '1fr 1fr' },
          gap: { xs: 2.5, md: 4 },
          touchAction: isMobileView ? 'pan-y' : undefined,
        }}
        onTouchStart={handleBookingPickerTouchStart}
        onTouchMove={handleBookingPickerTouchMove}
        onTouchEnd={handleBookingPickerTouchEnd}
      >
        {isMobileView ? (
          renderBookingPickerMonth(bookingPickerMonth, true, true)
        ) : (
          <>
            {renderBookingPickerMonth(bookingPickerMonth, true, false)}
            {renderBookingPickerMonth(bookingPickerMonth.add(1, 'month'), false, true)}
          </>
        )}
      </Box>
    </Stack>
  );

  const searchFilterCard = (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 1.25, sm: 2 },
        borderRadius: { xs: 3.5, sm: 3 },
        boxShadow: '0 20px 45px rgba(0,0,0,0.35)',
      }}
    >
      <Grid container spacing={{ xs: 1.5, sm: 2 }} alignItems="center">
        <Grid item xs={12} sm={6} md={5}>
          <TextField
            fullWidth
            required
            size="small"
            label={t('search.dateRangeLabel')}
            placeholder={t('search.dateRangeLabel')}
            value={dateRangeText}
            onClick={handleOpenBookingDatePicker}
            InputProps={{
              readOnly: true,
              startAdornment: (
                <InputAdornment position="start">
                  <CalendarMonth fontSize="small" />
                </InputAdornment>
              ),
            }}
            inputProps={{
              'aria-label': t('search.dateRangeLabel'),
            }}
            sx={{
              cursor: 'pointer',
              '& .MuiInputBase-root': {
                cursor: 'pointer',
              },
              '& input': {
                cursor: 'pointer',
              },
            }}
          />
          {isMobileView ? (
            <Dialog open={bookingPickerOpen} onClose={handleCloseBookingDatePicker} fullScreen>
              <DialogTitle
                sx={{
                  px: 2,
                  py: 1.5,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
                  <Box>
                    <Typography variant="h6" sx={{ lineHeight: 1.15, fontWeight: 800 }}>
                      {t('search.bookingCalendar')}
                    </Typography>
                  </Box>
                  <IconButton
                    edge="end"
                    aria-label={t('common.close')}
                    onClick={handleCloseBookingDatePicker}
                    sx={{ mt: -0.25 }}
                  >
                    <Close fontSize="small" />
                  </IconButton>
                </Stack>
              </DialogTitle>
              <DialogContent sx={{ px: 2, py: 2.5 }}>
                {bookingPickerContent}
              </DialogContent>
            </Dialog>
          ) : (
            <Popover
              open={bookingPickerOpen}
              anchorEl={bookingPickerAnchorEl}
              onClose={handleCloseBookingDatePicker}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              PaperProps={{
                sx: {
                  width: { xs: 'calc(100vw - 32px)', md: 760 },
                  maxWidth: 'calc(100vw - 32px)',
                  mt: 1,
                  borderRadius: 2,
                  boxShadow: '0 16px 36px rgba(15, 23, 42, 0.2)',
                },
              }}
            >
              <Box sx={{ p: 2.5 }}>
                <Box
                  sx={{
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Typography
                    variant="body2"
                    align="center"
                    sx={{
                      color: 'primary.main',
                      fontWeight: 600,
                      pb: 1.5,
                      borderBottom: '2px solid',
                      borderColor: 'primary.main',
                    }}
                  >
                    {t('search.bookingCalendar')}
                  </Typography>
                </Box>

                <Box sx={{ pt: 2.5, pb: 2 }}>
                  {bookingPickerContent}
                </Box>
              </Box>
            </Popover>
          )}
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <TextField
            fullWidth
            size="small"
            type="number"
            label={t('search.maxDailyRate')}
            value={maxRate}
            onChange={(event) => {
              setMaxRate(event.target.value);
              setSuccessReservation(null);
            }}
            InputProps={{
              startAdornment: <InputAdornment position="start">{CURRENCY_SYMBOL}</InputAdornment>,
            }}
            inputProps={{ min: 0, step: 1 }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel>{t('search.vehicleType')}</InputLabel>
            <Select
              label={t('search.vehicleType')}
              value={vehicleType}
              onChange={(event) => {
                setVehicleType(event.target.value as VehicleType | '');
                setSuccessReservation(null);
              }}
            >
              <MenuItem value="">{t('search.allTypes')}</MenuItem>
              {Object.values(VehicleType).map((type) => (
                <MenuItem key={type} value={type}>
                  {vehicleTypeLabel(t, type)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Button
            fullWidth
            size={isMobileView ? 'medium' : 'small'}
            onClick={resetFilters}
            sx={{ minHeight: isMobileView ? 42 : undefined, fontWeight: 700 }}
          >
            {t('common.clearFilters')}
          </Button>
        </Grid>
      </Grid>
    </Paper>
  );

  const successAlert = successReservation && (
    <Alert
      severity="success"
      sx={{ mb: 3 }}
      action={
        <Button color="inherit" size="small" onClick={() => setSuccessReservation(null)}>
          {t('common.close')}
        </Button>
      }
    >
      <Typography variant="subtitle2">
        {t('booking.messages.created', 'Your rental request is pending.')}
      </Typography>
      <Typography variant="body2">
        {t('booking.messages.contact', 'We will contact you by phone or email for the rental.')}{' '}
        {successReservation.reservationNumber
          ? t('booking.messages.reference', {
              defaultValue: 'Reference: {{number}}',
              number: successReservation.reservationNumber,
            })
          : null}
      </Typography>
    </Alert>
  );

  const handleScrollToSearch = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleLogoError = () => setLogoLoadError(true);

  const templateProps = {
    tenantCompany,
    tenantLogoUrl,
    logoLoadError,
    onLogoError: handleLogoError,
    quickFleet,
    tenantLocationSummary,
    tenantWhatsappHref,
    searchFilterCard,
    successAlert,
    renderResultBody,
    handleScrollToSearch,
    datesReady,
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box>
        <Box
          sx={{
            position: 'fixed',
            top: { xs: 'calc(env(safe-area-inset-top, 0px) + 10px)', sm: 16 },
            right: { xs: 10, sm: 16 },
            zIndex: (theme) => theme.zIndex.appBar,
          }}
        >
          <Paper
            elevation={3}
            sx={{
              borderRadius: 999,
              pl: { xs: 0.35, sm: 0.45 },
              pr: { xs: 0.2, sm: 0.3 },
              py: { xs: 0.15, sm: 0.25 },
              bgcolor: 'rgba(255,255,255,0.94)',
              color: 'text.primary',
              display: 'flex',
              alignItems: 'center',
              backdropFilter: 'blur(12px)',
              boxShadow: '0 12px 28px rgba(15, 23, 42, 0.18)',
            }}
          >
            <LanguageSwitcher />
          </Paper>
        </Box>

        {isTenantCompanyLoading ? (
          // Avoid flashing the generic fallback headline/no-logo state before the tenant's
          // real branding arrives - especially noticeable on slower first loads in production.
          <Box
            sx={{
              minHeight: '60vh',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CircularProgress size={32} />
          </Box>
        ) : isMinimalTemplate ? (
          <MinimalTemplate {...templateProps} />
        ) : (
          <ClassicTemplate {...templateProps} />
        )}

        <Box
          sx={{
            px: 2,
            pb: { xs: 4, sm: 5 },
            pt: { xs: 1.5, sm: 2.5 },
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <Button
            size="small"
            onClick={() => navigate(APP_ROUTES.auth.login)}
            sx={{
              color: 'text.secondary',
              opacity: 0.74,
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': {
                opacity: 1,
                bgcolor: 'transparent',
              },
            }}
          >
            {t('auth.staffLogin', 'Staff Login')}
          </Button>
        </Box>
      </Box>

        <Drawer
          anchor="right"
          open={Boolean(detailsVehicle)}
          onClose={handleCloseVehicleDetails}
          PaperProps={{
            sx: {
              width: { xs: '100%', sm: 440 },
              bgcolor: '#F4F7FB',
              backgroundImage: 'linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(244,247,251,1) 100%)',
            },
          }}
        >
          {detailsVehicle && (
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Box
                sx={{
                  px: 2.5,
                  pt: 2.5,
                  pb: 2.25,
                  color: 'common.white',
                  background: 'linear-gradient(135deg, #0A0F1C 0%, #16356C 100%)',
                }}
              >
                <Stack direction="row" spacing={1.5} alignItems="flex-start">
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="h5" sx={{ lineHeight: 1.08, fontWeight: 800 }}>
                      {detailsVehicle.make} {detailsVehicle.model}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ mt: 0.75, color: 'rgba(255,255,255,0.72)' }}
                      noWrap
                    >
                      {detailsVehicle.year}
                      {detailsVehicle.licensePlate ? ` - ${detailsVehicle.licensePlate}` : ''}
                    </Typography>
                  </Box>

                  <Stack direction="row" spacing={1} alignItems="flex-start">
                    <Box
                      sx={{
                        px: 1.5,
                        py: 1,
                        borderRadius: 2.5,
                        bgcolor: 'rgba(255,255,255,0.12)',
                        border: '1px solid rgba(255,255,255,0.14)',
                        textAlign: 'right',
                      }}
                    >
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.72)' }}>
                        {t('fields.dailyRate')}
                      </Typography>
                      <Typography variant="subtitle1" sx={{ fontWeight: 800, lineHeight: 1.1 }}>
                        {formatCurrency(detailsVehicleRate)}
                      </Typography>
                    </Box>

                    <IconButton
                      size="small"
                      aria-label={t('common.close')}
                      onClick={handleCloseVehicleDetails}
                      sx={{
                        color: 'common.white',
                        bgcolor: 'rgba(255,255,255,0.08)',
                        border: '1px solid rgba(255,255,255,0.12)',
                        '&:hover': { bgcolor: 'rgba(255,255,255,0.14)' },
                      }}
                    >
                      <Close fontSize="small" />
                    </IconButton>
                  </Stack>
                </Stack>
              </Box>

              <Box sx={{ p: 2.25, flex: 1, overflowY: 'auto' }}>
                <Stack spacing={2.25}>
                  <Box
                    sx={{
                      position: 'relative',
                      width: '100%',
                      height: 220,
                      borderRadius: 3,
                      bgcolor: 'grey.100',
                      overflow: 'hidden',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 18px 36px rgba(15, 23, 42, 0.16)',
                    }}
                  >
                    {detailsVehicle.imageUrls?.[0] ? (
                      <Box
                        component="img"
                        src={detailsVehicle.imageUrls[0]}
                        alt={`${detailsVehicle.make} ${detailsVehicle.model}`}
                        sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    ) : (
                      <DirectionsCar sx={{ fontSize: 68, color: 'text.disabled' }} />
                    )}

                    <Box
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        background: 'linear-gradient(180deg, rgba(10,15,28,0) 44%, rgba(10,15,28,0.76) 100%)',
                      }}
                    />

                    <Stack
                      direction="row"
                      spacing={1}
                      sx={{
                        position: 'absolute',
                        left: 16,
                        right: 16,
                        bottom: 16,
                        flexWrap: 'wrap',
                        rowGap: 1,
                      }}
                    >
                      <Chip
                        size="small"
                        label={vehicleTypeLabel(t, detailsVehicle.type)}
                        sx={{ bgcolor: 'rgba(255,255,255,0.94)', fontWeight: 700 }}
                      />
                      <Chip
                        size="small"
                        label={vehicleClassLabel(t, detailsVehicle.vehicleClass)}
                        sx={{ bgcolor: 'rgba(255,255,255,0.94)', fontWeight: 700 }}
                      />
                      <Chip
                        size="small"
                        label={`${detailsVehicle.seatingCapacity || '-'} ${t('fields.seats')}`}
                        sx={{ bgcolor: 'rgba(255,255,255,0.94)', fontWeight: 700 }}
                      />
                    </Stack>
                  </Box>

                  <Paper
                    variant="outlined"
                    sx={{
                      borderRadius: 3,
                      p: 2,
                      borderColor: 'rgba(37, 99, 235, 0.14)',
                      background: 'linear-gradient(180deg, rgba(37,99,235,0.06) 0%, rgba(255,255,255,0.98) 100%)',
                    }}
                  >
                    {detailsVehicleEstimatedTotal !== null ? (
                      <Stack spacing={1.5}>
                        <Stack direction="row" spacing={2} justifyContent="space-between" alignItems="flex-end">
                          <Box>
                            <Typography variant="caption" color="text.secondary">
                              {t('fields.dailyRate')}
                            </Typography>
                            <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1.1 }}>
                              {formatCurrency(detailsVehicleRate)}
                            </Typography>
                          </Box>
                          <Box sx={{ textAlign: 'right' }}>
                            <Typography variant="caption" color="text.secondary">
                              {t('fields.estimatedTotal')}
                            </Typography>
                            <Typography variant="h4" color="primary.main" sx={{ fontWeight: 800, lineHeight: 1.05 }}>
                              {formatCurrency(detailsVehicleEstimatedTotal)}
                            </Typography>
                          </Box>
                        </Stack>

                        <Divider />

                        <Stack spacing={1}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                            <Typography variant="body2" color="text.secondary">
                              {t('fields.rentalPeriod')}
                            </Typography>
                            <Typography variant="body2" fontWeight={700} textAlign="right">
                              {dateRangeText}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                            <Typography variant="body2" color="text.secondary">
                              {t('fields.duration')}
                            </Typography>
                            <Typography variant="body2" fontWeight={700}>
                              {t('booking.steps.rentalDaysCount', {
                                count: rentalDays,
                                defaultValue: `${rentalDays} days`,
                              })}
                            </Typography>
                          </Box>
                        </Stack>
                      </Stack>
                    ) : (
                      <Stack direction="row" spacing={2} justifyContent="space-between" alignItems="flex-end">
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            {t('fields.dailyRate')}
                          </Typography>
                          <Typography variant="h4" color="primary.main" sx={{ fontWeight: 800, lineHeight: 1.05 }}>
                            {formatCurrency(detailsVehicleRate)}
                          </Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ pb: 0.5 }}>
                          / {t('search.perDay', 'day')}
                        </Typography>
                      </Stack>
                    )}
                  </Paper>

                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                      gap: 1.25,
                    }}
                  >
                    {detailsVehicleSpecs.map((item) => (
                      <Paper
                        key={item.label}
                        variant="outlined"
                        sx={{
                          p: 1.5,
                          borderRadius: 2.5,
                          bgcolor: 'rgba(255,255,255,0.86)',
                        }}
                      >
                        <Stack direction="row" spacing={1.25} alignItems="center">
                          <Box
                            sx={{
                              width: 38,
                              height: 38,
                              borderRadius: 2,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              bgcolor: 'rgba(37,99,235,0.08)',
                              color: 'primary.main',
                              flexShrink: 0,
                            }}
                          >
                            {item.icon}
                          </Box>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="caption" color="text.secondary">
                              {item.label}
                            </Typography>
                            <Typography variant="subtitle2" sx={{ lineHeight: 1.2, wordBreak: 'break-word' }}>
                              {item.value}
                            </Typography>
                          </Box>
                        </Stack>
                      </Paper>
                    ))}
                  </Box>

                  {detailsVehicle.description ? (
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 2,
                        borderRadius: 3,
                        bgcolor: 'rgba(255,255,255,0.9)',
                      }}
                    >
                      <Typography variant="subtitle2" sx={{ mb: 0.75 }}>
                        {t('fields.description', 'Description')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                        {detailsVehicle.description}
                      </Typography>
                    </Paper>
                  ) : null}
                </Stack>
              </Box>

              <Box
                sx={{
                  p: 2.25,
                  borderTop: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'rgba(255,255,255,0.92)',
                  backdropFilter: 'blur(10px)',
                }}
              >
                <Button
                  variant="contained"
                  fullWidth
                  size="large"
                  startIcon={<EventAvailable fontSize="small" />}
                  onClick={() => handleVehicleSelect(detailsVehicle)}
                  sx={{ minHeight: 48 }}
                >
                  {t('booking.bookRent', 'Book Rent')}
                </Button>
              </Box>
            </Box>
          )}
        </Drawer>

        <Dialog
          open={bookingOpen}
          onClose={handleCloseBooking}
          maxWidth="md"
          fullWidth
          fullScreen={isMobileView}
          PaperProps={{
            sx: isMobileView
              ? { bgcolor: 'background.default' }
              : { borderRadius: 3 },
          }}
        >
          <Box
            component="form"
            noValidate
            onSubmit={handleBookingFormSubmit}
            sx={{ height: isMobileView ? '100%' : 'auto', display: 'flex', flexDirection: 'column' }}
          >
            <DialogTitle
              sx={{
                px: { xs: 2, sm: 3 },
                py: { xs: 1.5, sm: 2 },
                borderBottom: isMobileView ? '1px solid' : 'none',
                borderColor: 'divider',
                bgcolor: 'background.paper',
              }}
            >
              <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center">
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.15 }}>
                    {t('booking.bookRent', 'Book Rent')}
                  </Typography>
                  {isMobileView && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35 }}>
                      {activeBookingStepLabel}
                    </Typography>
                  )}
                </Box>
                <IconButton
                  aria-label={t('common.close')}
                  onClick={handleCloseBooking}
                  disabled={createBookingMutation.isLoading || isDocumentBusy}
                >
                  <Close fontSize="small" />
                </IconButton>
              </Stack>
            </DialogTitle>
            <DialogContent
              dividers={!isMobileView}
              sx={{
                px: { xs: 2, sm: 3 },
                py: { xs: 2, sm: 2.5 },
                flex: 1,
                bgcolor: isMobileView ? 'background.default' : 'background.paper',
              }}
            >
              {selectedVehicle && (
                <Paper
                  variant="outlined"
                  sx={{
                    mb: 2.5,
                    p: { xs: 1.5, sm: 2 },
                    borderRadius: 3,
                    bgcolor: 'rgba(255,255,255,0.92)',
                  }}
                >
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1.25}
                    justifyContent="space-between"
                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                  >
                    <Box>
                      <Typography variant="subtitle1" fontWeight={600}>
                        {selectedVehicle.make} {selectedVehicle.model} ({selectedVehicle.year})
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
                      <Typography variant="body2" color="text.secondary">
                        {startDate ? formatDate(startDate) : ''} - {endDate ? formatDate(endDate) : ''}
                      </Typography>
                      <Typography variant="subtitle2" color="primary">
                        {formatCurrency(getVehicleRate(selectedVehicle) * rentalDays)}
                      </Typography>
                    </Box>
                  </Stack>
                </Paper>
              )}

              {isMobileView ? (
                <Stack spacing={1.25} sx={{ mb: 2.5 }}>
                  <Stack direction="row" alignItems="center">
                    {bookingStepLabels.map((label, index) => (
                      <React.Fragment key={label}>
                        <Box
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '0.875rem',
                            fontWeight: 800,
                            flexShrink: 0,
                            color: index <= bookingStep ? 'primary.contrastText' : 'text.secondary',
                            bgcolor: index <= bookingStep ? 'primary.main' : 'action.selected',
                          }}
                        >
                          {index + 1}
                        </Box>
                        {index < bookingStepLabels.length - 1 && (
                          <Box
                            sx={{
                              flex: 1,
                              height: 2,
                              mx: 1,
                              borderRadius: 999,
                              bgcolor: index < bookingStep ? 'primary.main' : 'divider',
                            }}
                          />
                        )}
                      </React.Fragment>
                    ))}
                  </Stack>
                </Stack>
              ) : (
                <>
                  <Divider sx={{ mb: 2 }} />

                  <Stepper activeStep={bookingStep} sx={{ mb: 3 }}>
                    {bookingStepLabels.map((label) => (
                      <Step key={label}>
                        <StepLabel>{label}</StepLabel>
                      </Step>
                    ))}
                  </Stepper>
                </>
              )}

              {formError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {formError}
                </Alert>
              )}

              {bookingStep === 0 && (
              <>
              <Stack spacing={isMobileView ? 1.75 : 2} sx={{ mb: 2 }}>
                <input
                  ref={frontLicenseInputRef}
                  hidden
                  type="file"
                  accept="image/*"
                  onChange={(event) => handleDocumentFileSelected('front', event.target.files?.[0] || null)}
                />
                <input
                  ref={frontLicenseCameraInputRef}
                  hidden
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={(event) => handleDocumentFileSelected('front', event.target.files?.[0] || null)}
                />
                <input
                  ref={backLicenseInputRef}
                  hidden
                  type="file"
                  accept="image/*"
                  onChange={(event) => handleDocumentFileSelected('back', event.target.files?.[0] || null)}
                />

                <Box sx={{ width: '100%' }}>
                  <Paper
                    variant="outlined"
                    onClick={() => triggerFrontLicensePicker('gallery')}
                    onDragOver={(event) => {
                      event.preventDefault();
                      setDraggingSide('front');
                    }}
                    onDragLeave={() => setDraggingSide((current) => (current === 'front' ? null : current))}
                    onDrop={(event) => handleDriverLicenseDrop('front', event)}
                    sx={{
                      width: '100%',
                      px: { xs: 1.75, sm: 2.25 },
                      py: { xs: 2, sm: 2.5 },
                      minHeight: { xs: 150, sm: 176 },
                      borderStyle: 'dashed',
                      borderWidth: 2,
                      borderColor: draggingSide === 'front' ? 'primary.main' : 'divider',
                      bgcolor: draggingSide === 'front' ? 'rgba(25, 118, 210, 0.06)' : 'background.paper',
                      cursor: 'pointer',
                      position: 'relative',
                      overflow: 'hidden',
                      borderRadius: 3,
                    }}
                  >
                    {frontLicensePreviewUrl ? (
                      <Box sx={{ position: 'relative', height: '100%' }}>
                        <IconButton
                          size="small"
                          onClick={(event) => {
                            event.stopPropagation();
                            handleRemoveDriverLicenseFile('front');
                          }}
                          sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            zIndex: 1,
                            bgcolor: 'rgba(255,255,255,0.92)',
                          }}
                        >
                          <Close fontSize="small" />
                        </IconButton>
                        <Box
                          component="img"
                          src={frontLicensePreviewUrl}
                          alt={t('booking.document.frontPreview', 'Driver license preview')}
                          sx={{
                            width: '100%',
                            height: { xs: 112, sm: 132 },
                            objectFit: 'cover',
                            borderRadius: 2,
                            mb: 1.25,
                          }}
                        />
                        <Typography variant="subtitle2">
                          {t('booking.document.frontLabel', 'Driver license')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {frontLicenseFile?.name}
                        </Typography>
                        <Stack
                          direction={{ xs: 'column', sm: 'row' }}
                          spacing={1}
                          sx={{ mt: 1.5 }}
                        >
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<PhotoLibrary fontSize="small" />}
                            onClick={(event) => {
                              event.stopPropagation();
                              triggerFrontLicensePicker('gallery');
                            }}
                            sx={{ whiteSpace: 'nowrap' }}
                          >
                            {t('booking.document.galleryAction', 'Choose from gallery')}
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<PhotoCamera fontSize="small" />}
                            onClick={(event) => {
                              event.stopPropagation();
                              triggerFrontLicensePicker('camera');
                            }}
                            sx={{ whiteSpace: 'nowrap' }}
                          >
                            {t('booking.document.cameraAction', 'Take photo')}
                          </Button>
                        </Stack>
                      </Box>
                    ) : (
                      <Stack spacing={1.25} alignItems="center" justifyContent="center" sx={{ height: '100%', textAlign: 'center' }}>
                        <CloudUpload color="primary" sx={{ fontSize: 34 }} />
                        <Typography variant="subtitle1" fontWeight={700}>
                          {t('booking.document.frontUpload', 'Upload driver license')}
                        </Typography>
                        <Stack
                          direction={{ xs: 'column', sm: 'row' }}
                          spacing={1}
                          sx={{ width: '100%', maxWidth: { xs: 320, sm: 380 }, pt: 0.5 }}
                        >
                          <Button
                            fullWidth
                            size="small"
                            variant="outlined"
                            startIcon={<PhotoLibrary fontSize="small" />}
                            onClick={(event) => {
                              event.stopPropagation();
                              triggerFrontLicensePicker('gallery');
                            }}
                            sx={{ whiteSpace: 'nowrap' }}
                          >
                            {t('booking.document.galleryAction', 'Choose from gallery')}
                          </Button>
                          <Button
                            fullWidth
                            size="small"
                            variant="contained"
                            startIcon={<PhotoCamera fontSize="small" />}
                            onClick={(event) => {
                              event.stopPropagation();
                              triggerFrontLicensePicker('camera');
                            }}
                            sx={{ whiteSpace: 'nowrap' }}
                          >
                            {t('booking.document.cameraAction', 'Take photo')}
                          </Button>
                        </Stack>
                      </Stack>
                    )}
                  </Paper>
                </Box>

                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1.25}
                  alignItems={{ xs: 'stretch', sm: 'center' }}
                  justifyContent="center"
                >
                  <Button
                    variant="outlined"
                    fullWidth={isMobileView}
                    startIcon={isDocumentBusy ? <CircularProgress size={16} color="inherit" /> : <CloudUpload />}
                    onClick={handleUploadDriverLicense}
                    disabled={!frontLicenseFile || isDocumentBusy || createBookingMutation.isLoading}
                  >
                    {isDocumentBusy
                      ? t('booking.document.processing', 'Processing document...')
                      : t('booking.document.processAction', 'Upload and auto-fill')}
                  </Button>

                  {licenseExtraction?.overallConfidence != null && (
                    <Chip
                      color={licenseProcessingState === 'completed' ? 'success' : 'default'}
                      label={t('booking.document.confidence', {
                        defaultValue: 'AI confidence: {{percent}}%',
                        percent: Math.round((licenseExtraction.overallConfidence || 0) * 100),
                      })}
                    />
                  )}

                  {licenseDocumentToken && !isDocumentBusy && (
                    <Chip
                      color="primary"
                      variant="outlined"
                      label={t('booking.document.secureSaved', 'Document attached securely')}
                    />
                  )}
                </Stack>

                {isDocumentBusy && (
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75 }}>
                      {licenseStatusMessage || t('booking.document.processing', 'Processing document...')}
                    </Typography>
                    <LinearProgress
                      variant={licenseProcessingState === 'extracting' ? 'indeterminate' : 'determinate'}
                      value={licenseProcessingState === 'extracting' ? undefined : licenseUploadProgress}
                    />
                  </Box>
                )}

                {licenseStatusMessage && !isDocumentBusy && (
                  <Alert severity={licenseProcessingState === 'completed' ? 'success' : (licenseProcessingState === 'failed' ? 'warning' : 'info')}>
                    {licenseStatusMessage}
                  </Alert>
                )}
              </Stack>

              <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    required
                    size="small"
                    name="firstName"
                    label={t('fields.firstName')}
                    autoComplete="given-name"
                    inputRef={firstNameFieldRef}
                    error={isDriverFieldMissing('firstName')}
                    value={bookingForm.firstName}
                    onChange={(event) => handleBookingFormChange('firstName', event.target.value)}
                    onKeyDown={(event) => handleAdvanceBookingField(event, lastNameFieldRef)}
                    inputProps={{
                      enterKeyHint: 'next',
                      autoCapitalize: 'words',
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    required
                    size="small"
                    name="lastName"
                    label={t('fields.lastName')}
                    autoComplete="family-name"
                    inputRef={lastNameFieldRef}
                    error={isDriverFieldMissing('lastName')}
                    value={bookingForm.lastName}
                    onChange={(event) => handleBookingFormChange('lastName', event.target.value)}
                    onKeyDown={(event) => handleAdvanceBookingField(event, personalIdFieldRef)}
                    inputProps={{
                      enterKeyHint: 'next',
                      autoCapitalize: 'words',
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    required
                    size="small"
                    name="personalIdNumber"
                    label={t('fields.personalIdNumber')}
                    inputRef={personalIdFieldRef}
                    error={isDriverFieldMissing('personalIdNumber')}
                    spellCheck={false}
                    value={bookingForm.personalIdNumber}
                    onChange={(event) => handleBookingFormChange('personalIdNumber', event.target.value)}
                    onKeyDown={(event) => handleAdvanceBookingField(event, dateOfBirthFieldRef)}
                    inputProps={{
                      enterKeyHint: 'next',
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <DateInputField
                    fullWidth
                    required
                    size="small"
                    label={t('fields.dateOfBirth')}
                    autoComplete="bday"
                    inputRef={dateOfBirthFieldRef}
                    error={isDriverFieldMissing('dateOfBirth')}
                    inputProps={{
                      enterKeyHint: 'next',
                      inputMode: 'numeric',
                    }}
                    value={bookingForm.dateOfBirth}
                    onChange={(dateOfBirth) => handleBookingFormChange('dateOfBirth', dateOfBirth)}
                    onKeyDown={(event) => handleAdvanceBookingField(event, licenseNumberFieldRef)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    required
                    size="small"
                    name="driverLicenseNumber"
                    label={t('fields.licenseNumber')}
                    autoComplete="off"
                    inputRef={licenseNumberFieldRef}
                    error={isDriverFieldMissing('driverLicenseNumber')}
                    spellCheck={false}
                    value={bookingForm.driverLicenseNumber}
                    onChange={(event) => handleBookingFormChange('driverLicenseNumber', event.target.value)}
                    onKeyDown={(event) => handleAdvanceBookingField(event, licenseIssueDateFieldRef)}
                    inputProps={{
                      enterKeyHint: 'next',
                      autoCapitalize: 'characters',
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <DateInputField
                    fullWidth
                    required
                    size="small"
                    label={t('fields.licenseIssueDate', 'License Issue Date')}
                    autoComplete="off"
                    inputRef={licenseIssueDateFieldRef}
                    error={isDriverFieldMissing('licenseIssueDate')}
                    inputProps={{
                      enterKeyHint: 'next',
                      inputMode: 'numeric',
                    }}
                    value={bookingForm.licenseIssueDate}
                    onChange={(licenseIssueDate) =>
                      handleBookingFormChange('licenseIssueDate', licenseIssueDate)
                    }
                    onKeyDown={(event) => handleAdvanceBookingField(event, licenseExpiryDateFieldRef)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <DateInputField
                    fullWidth
                    required
                    size="small"
                    label={t('fields.expiryDate')}
                    autoComplete="off"
                    inputRef={licenseExpiryDateFieldRef}
                    error={isDriverFieldMissing('licenseExpiryDate')}
                    inputProps={{
                      enterKeyHint: 'next',
                      inputMode: 'numeric',
                    }}
                    value={bookingForm.licenseExpiryDate}
                    onChange={(licenseExpiryDate) =>
                      handleBookingFormChange('licenseExpiryDate', licenseExpiryDate)
                    }
                    onKeyDown={(event) => handleAdvanceBookingField(event, licenseCategoriesFieldRef)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    required
                    size="small"
                    name="licenseCategories"
                    label={t('fields.licenseCategories', 'License Categories')}
                    autoComplete="off"
                    inputRef={licenseCategoriesFieldRef}
                    error={isDriverFieldMissing('licenseCategories')}
                    spellCheck={false}
                    value={bookingForm.licenseCategories}
                    onChange={(event) => handleBookingFormChange('licenseCategories', event.target.value)}
                    onKeyDown={(event) => handleAdvanceBookingField(event, phoneFieldRef)}
                    inputProps={{
                      enterKeyHint: 'next',
                      autoCapitalize: 'characters',
                    }}
                    helperText={
                      licenseExtraction?.driverLicense?.entitlements?.length
                        ? licenseExtraction.driverLicense.entitlements
                            .map((entitlement) =>
                              [entitlement.categoryCode, entitlement.validFrom, entitlement.validTo]
                                .filter(Boolean)
                                .join(' ')
                            )
                            .join(' | ')
                        : t('booking.document.licenseCategoriesHint', 'Example: B or B, BE')
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    required
                    size="small"
                    name="phoneNumber"
                    label={t('fields.phoneNumber')}
                    autoComplete="tel"
                    inputRef={phoneFieldRef}
                    error={isDriverFieldMissing('phoneNumber')}
                    value={bookingForm.phoneNumber}
                    onChange={(event) => handleBookingFormChange('phoneNumber', event.target.value)}
                    onKeyDown={(event) => handleAdvanceBookingField(event, emailFieldRef)}
                    inputProps={{
                      enterKeyHint: 'next',
                      inputMode: 'tel',
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    name="email"
                    label={t('fields.emailOptional', 'Email (optional)')}
                    type="email"
                    autoComplete="email"
                    inputRef={emailFieldRef}
                    error={isDriverEmailInvalid}
                    helperText={isDriverEmailInvalid ? t('validation.email') : undefined}
                    value={bookingForm.email}
                    onChange={(event) => handleBookingFormChange('email', event.target.value)}
                    onKeyDown={(event) => handleAdvanceBookingField(event, specialRequestsFieldRef)}
                    inputProps={{
                      enterKeyHint: 'next',
                      inputMode: 'email',
                      autoCapitalize: 'off',
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    label={t('fields.specialRequestsOptional')}
                    autoComplete="off"
                    inputRef={specialRequestsFieldRef}
                    value={bookingForm.specialRequests}
                    onChange={(event) => handleBookingFormChange('specialRequests', event.target.value)}
                    multiline
                    minRows={3}
                    inputProps={{
                      enterKeyHint: 'done',
                    }}
                  />
                </Grid>
              </Grid>
              </>
              )}

              {bookingStep === 1 && (
                <Stack spacing={2.5}>
                  <Alert severity="info">
                    {t(
                      'booking.steps.locationHelper',
                      'Confirm where the vehicle will be picked up and returned. You can adjust the rental dates from the search filters behind this dialog.'
                    )}
                  </Alert>

                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      spacing={1}
                      justifyContent="space-between"
                      alignItems={{ xs: 'flex-start', sm: 'center' }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center">
                        <CalendarMonth fontSize="small" color="action" />
                        <Box>
                          <Typography variant="body2" fontWeight={600}>
                            {startDate ? formatDate(startDate) : ''} - {endDate ? formatDate(endDate) : ''}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {t('booking.steps.rentalDaysCount', {
                              defaultValue: '{{count}} day(s)',
                              count: rentalDays,
                            })}
                          </Typography>
                        </Box>
                      </Stack>
                      <Button size="small" onClick={handleOpenBookingDatePicker}>
                        {t('booking.steps.changeDates', 'Change dates')}
                      </Button>
                    </Stack>
                  </Paper>

                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: '1fr',
                        sm: 'repeat(2, minmax(0, 1fr))',
                      },
                      gap: 1.5,
                    }}
                  >
                    <Box>
                      <TextField
                        fullWidth
                        required
                        size="small"
                        type="time"
                        label={t('fields.startTime')}
                        value={pickupTime}
                        onChange={(event) => setPickupTime(event.target.value)}
                        error={isPickupTimeMissing}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ step: 300 }}
                        inputRef={pickupTimeFieldRef}
                        sx={BOOKING_TIME_FIELD_SX}
                      />
                    </Box>
                    <Box>
                      <TextField
                        fullWidth
                        required
                        size="small"
                        type="time"
                        label={t('fields.endTime')}
                        value={returnTime}
                        onChange={(event) => setReturnTime(event.target.value)}
                        error={isReturnTimeMissing}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ step: 300 }}
                        inputRef={returnTimeFieldRef}
                        sx={BOOKING_TIME_FIELD_SX}
                      />
                    </Box>
                  </Box>

                  {renderLocationSelect({
                    label: t('fields.pickupLocation', 'Pickup location'),
                    otherLabel: t('booking.steps.otherLocationDetail', 'Enter the pickup address'),
                    idValue: pickupLocationId,
                    onIdChange: setPickupLocationId,
                    otherValue: pickupLocationOther,
                    onOtherChange: setPickupLocationOther,
                    hasError: isPickupLocationMissing,
                    hasOtherError: isPickupLocationOtherMissing,
                  })}

                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={returnSameAsPickup}
                        onChange={(event) => setReturnSameAsPickup(event.target.checked)}
                      />
                    }
                    label={t('booking.steps.returnSameLocation', 'Return to the same location')}
                  />

                  {!returnSameAsPickup &&
                    renderLocationSelect({
                      label: t('fields.returnLocation', 'Return location'),
                      otherLabel: t('booking.steps.otherLocationDetail', 'Enter the return address'),
                      idValue: returnLocationId,
                      onIdChange: setReturnLocationId,
                      otherValue: returnLocationOther,
                      onOtherChange: setReturnLocationOther,
                      hasError: isReturnLocationMissing,
                      hasOtherError: isReturnLocationOtherMissing,
                    })}
                </Stack>
              )}

              {bookingStep === 2 && (
                <Stack spacing={2.5}>
                  <Alert severity="info">
                    {t('booking.steps.reviewHelper', 'Please check everything below before submitting your booking request.')}
                  </Alert>

                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
                      {t('booking.steps.reviewVehicleTitle', 'Vehicle & dates')}
                    </Typography>
                    <Grid container spacing={1.5}>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">{t('fields.vehicle', 'Vehicle')}</Typography>
                        <Typography variant="body2">
                          {selectedVehicle ? `${selectedVehicle.make} ${selectedVehicle.model} (${selectedVehicle.year})` : ''}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">{t('fields.estimatedTotal')}</Typography>
                        <Typography variant="body2" fontWeight={600} color="primary.main">
                          {selectedVehicle ? formatCurrency(getVehicleRate(selectedVehicle) * rentalDays) : ''}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">{t('fields.startDate', 'Start date')}</Typography>
                        <Typography variant="body2">{startDate ? formatDate(startDate) : ''}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">{t('fields.endDate')}</Typography>
                        <Typography variant="body2">{endDate ? formatDate(endDate) : ''}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">{t('fields.startTime')}</Typography>
                        <Typography variant="body2">{pickupTime}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">{t('fields.endTime')}</Typography>
                        <Typography variant="body2">{returnTime}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">{t('fields.pickupLocation', 'Pickup location')}</Typography>
                        <Typography variant="body2">{pickupLocationText}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">{t('fields.returnLocation', 'Return location')}</Typography>
                        <Typography variant="body2">{returnLocationText}</Typography>
                      </Grid>
                    </Grid>
                  </Paper>

                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                      <Typography variant="subtitle2" fontWeight={700}>
                        {t('booking.steps.reviewDriverTitle', 'Driver details')}
                      </Typography>
                      <Button size="small" onClick={() => setBookingStep(0)}>
                        {t('common.edit', 'Edit')}
                      </Button>
                    </Stack>
                    <Grid container spacing={1.5}>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">{t('fields.firstName')}</Typography>
                        <Typography variant="body2">{bookingForm.firstName}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">{t('fields.lastName')}</Typography>
                        <Typography variant="body2">{bookingForm.lastName}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">{t('fields.personalIdNumber')}</Typography>
                        <Typography variant="body2">{bookingForm.personalIdNumber}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">{t('fields.dateOfBirth')}</Typography>
                        <Typography variant="body2">{bookingForm.dateOfBirth}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">{t('fields.phoneNumber')}</Typography>
                        <Typography variant="body2">{bookingForm.phoneNumber}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">{t('fields.emailOptional', 'Email (optional)')}</Typography>
                        <Typography variant="body2">{bookingForm.email || '—'}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">{t('fields.licenseNumber')}</Typography>
                        <Typography variant="body2">{bookingForm.driverLicenseNumber}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">{t('fields.licenseCategories', 'License Categories')}</Typography>
                        <Typography variant="body2">{bookingForm.licenseCategories}</Typography>
                      </Grid>
                      {bookingForm.specialRequests && (
                        <Grid item xs={12}>
                          <Typography variant="caption" color="text.secondary">{t('fields.specialRequestsOptional')}</Typography>
                          <Typography variant="body2">{bookingForm.specialRequests}</Typography>
                        </Grid>
                      )}
                    </Grid>
                    {licenseDocumentToken && (
                      <Chip
                        sx={{ mt: 1.5 }}
                        size="small"
                        color="primary"
                        variant="outlined"
                        label={t('booking.document.secureSaved', 'Document attached securely')}
                      />
                    )}
                  </Paper>
                </Stack>
              )}
            </DialogContent>
            {isMobileView ? (
              <DialogActions
                sx={{
                  px: 2,
                  py: 1.5,
                  borderTop: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'background.paper',
                }}
              >
                <Stack spacing={1} sx={{ width: '100%' }}>
                  {bookingStep > 0 && (
                    <Button
                      fullWidth
                      onClick={handleBookingBack}
                      disabled={createBookingMutation.isLoading || isDocumentBusy}
                    >
                      {t('common.back', 'Back')}
                    </Button>
                  )}
                  <Stack direction="row" spacing={1}>
                    <Button
                      fullWidth
                      onClick={handleCloseBooking}
                      disabled={createBookingMutation.isLoading || isDocumentBusy}
                    >
                      {t('common.cancel')}
                    </Button>
                    <Button
                      type="submit"
                      fullWidth
                      variant="contained"
                      disabled={createBookingMutation.isLoading || isDocumentBusy}
                      startIcon={
                        createBookingMutation.isLoading ? (
                          <CircularProgress size={16} color="inherit" />
                        ) : bookingStep === 2 ? (
                          <EventAvailable fontSize="small" />
                        ) : undefined
                      }
                    >
                      {bookingStep === 2 ? t('booking.bookRent', 'Book Rent') : t('common.next', 'Next')}
                    </Button>
                  </Stack>
                </Stack>
              </DialogActions>
            ) : (
              <DialogActions sx={{ px: 3, pb: 2.5 }}>
                <Button onClick={handleCloseBooking} disabled={createBookingMutation.isLoading || isDocumentBusy}>
                  {t('common.cancel')}
                </Button>
                <Box sx={{ flexGrow: 1 }} />
                {bookingStep > 0 && (
                  <Button onClick={handleBookingBack} disabled={createBookingMutation.isLoading || isDocumentBusy}>
                    {t('common.back', 'Back')}
                  </Button>
                )}
                <Button
                  type="submit"
                  variant="contained"
                  disabled={createBookingMutation.isLoading || isDocumentBusy}
                  startIcon={
                    createBookingMutation.isLoading ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : bookingStep === 2 ? (
                      <EventAvailable fontSize="small" />
                    ) : undefined
                  }
                >
                  {bookingStep === 2 ? t('booking.bookRent', 'Book Rent') : t('common.next', 'Next')}
                </Button>
              </DialogActions>
            )}
          </Box>
        </Dialog>
    </LocalizationProvider>
  );
};
